/**
 * Atmospheric Sounding & Vertical Profile Component
 *
 * Renders an interactive thermodynamic vertical profile (Skew-T / log-p style)
 * displaying temperature, dew point, moisture saturation (clouds),
 * temperature inversions, freezing level, and winds aloft across isobaric
 * surfaces (Surface, 925, 850, 700, 500, 300 hPa).
 */

import type { AtmosphericColumn, InversionMetrics } from '../types';

export interface SoundingChartOptions {
  container: HTMLElement;
  soundings: AtmosphericColumn[];
  inversionMetrics: InversionMetrics[];
  poiElevFt: number;
  minElevFt: number;
  maxElevFt: number;
  isImperial: boolean;
  initialHourIdx?: number;
  onHourChange?: (hourIdx: number) => void;
}

export class SoundingChart {
  private container: HTMLElement;
  private soundings: AtmosphericColumn[];
  private inversionMetrics: InversionMetrics[];
  private poiElevFt: number;
  private minElevFt: number;
  private maxElevFt: number;
  private isImperial: boolean;
  private activeHourIdx: number = 0;
  private isPlaying: boolean = false;
  private playIntervalId: number | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private hoverNode: { x: number; y: number; text: string[] } | null = null;
  private onHourChange?: (hourIdx: number) => void;
  private resizeObserver: ResizeObserver | null = null;

  constructor(options: SoundingChartOptions) {
    this.container = options.container;
    this.soundings = options.soundings;
    this.inversionMetrics = options.inversionMetrics;
    this.poiElevFt = options.poiElevFt;
    this.minElevFt = options.minElevFt;
    this.maxElevFt = options.maxElevFt;
    this.isImperial = options.isImperial;
    this.activeHourIdx = options.initialHourIdx ?? 0;
    this.onHourChange = options.onHourChange;

    this.render();
  }

  public setHour(hourIdx: number): void {
    if (hourIdx < 0 || hourIdx >= this.soundings.length) return;
    this.activeHourIdx = hourIdx;
    this.updateControls();
    this.drawChart();
    this.updateSummaryTable();
    if (this.onHourChange) {
      this.onHourChange(this.activeHourIdx);
    }
  }

  public destroy(): void {
    if (this.playIntervalId !== null) {
      window.clearInterval(this.playIntervalId);
      this.playIntervalId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.container.innerHTML = '';
  }

  private render(): void {
    this.container.innerHTML = '';
    this.container.style.display = 'flex';
    this.container.style.flexDirection = 'column';
    this.container.style.gap = '10px';
    this.container.style.color = '#f8fafc';
    this.container.style.fontFamily = 'Inter, system-ui, -apple-system, sans-serif';

    // Top Header & Inversion Badge
    const headerEl = document.createElement('div');
    headerEl.id = 'sounding-header';
    headerEl.style.display = 'flex';
    headerEl.style.justifyContent = 'space-between';
    headerEl.style.alignItems = 'center';
    headerEl.style.flexWrap = 'wrap';
    headerEl.style.gap = '8px';
    headerEl.style.borderBottom = '1px solid rgba(255, 255, 255, 0.08)';
    headerEl.style.paddingBottom = '8px';

    const titleDiv = document.createElement('div');
    titleDiv.innerHTML = `
      <div style="font-size: 0.95rem; font-weight: 600; color: #f8fafc; display: flex; align-items: center; gap: 8px;">
        <span>Vertical Atmospheric Sounding</span>
        <span style="font-size: 0.72rem; color: #94a3b8; font-weight: normal; background: rgba(255,255,255,0.06); padding: 2px 6px; border-radius: 4px;">Log-P Profile</span>
      </div>
      <div style="font-size: 0.75rem; color: #94a3b8; margin-top: 2px;">
        Multi-level isobaric columns (Surface to 300 hPa / ~30,000 ft)
      </div>
    `;

    const badgeDiv = document.createElement('div');
    badgeDiv.id = 'sounding-inversion-badge';
    headerEl.appendChild(titleDiv);
    headerEl.appendChild(badgeDiv);
    this.container.appendChild(headerEl);

    // Interactive Timeline Scrubber & Quick Actions
    const controlsEl = document.createElement('div');
    controlsEl.id = 'sounding-controls';
    controlsEl.style.display = 'flex';
    controlsEl.style.flexDirection = 'column';
    controlsEl.style.gap = '6px';
    controlsEl.style.background = 'rgba(0, 0, 0, 0.25)';
    controlsEl.style.padding = '8px 12px';
    controlsEl.style.borderRadius = '6px';
    controlsEl.style.border = '1px solid rgba(255, 255, 255, 0.05)';

    const scrubberRow = document.createElement('div');
    scrubberRow.style.display = 'flex';
    scrubberRow.style.alignItems = 'center';
    scrubberRow.style.gap = '10px';

    const playBtn = document.createElement('button');
    playBtn.id = 'sounding-play-btn';
    playBtn.textContent = 'Play';
    playBtn.style.padding = '4px 10px';
    playBtn.style.fontSize = '0.75rem';
    playBtn.style.borderRadius = '4px';
    playBtn.style.border = '1px solid rgba(255, 255, 255, 0.2)';
    playBtn.style.background = 'rgba(59, 130, 246, 0.3)';
    playBtn.style.color = '#93c5fd';
    playBtn.style.cursor = 'pointer';
    playBtn.style.fontWeight = 'bold';
    playBtn.style.minWidth = '52px';
    playBtn.onclick = () => this.togglePlay();

    const slider = document.createElement('input');
    slider.id = 'sounding-slider';
    slider.type = 'range';
    slider.min = '0';
    slider.max = String(Math.max(0, this.soundings.length - 1));
    slider.value = String(this.activeHourIdx);
    slider.style.flex = '1';
    slider.style.accentColor = '#3b82f6';
    slider.style.cursor = 'pointer';
    slider.oninput = (e) => {
      const idx = parseInt((e.target as HTMLInputElement).value, 10);
      this.setHour(idx);
    };

    const timeLabel = document.createElement('span');
    timeLabel.id = 'sounding-time-display';
    timeLabel.style.fontSize = '0.78rem';
    timeLabel.style.fontWeight = 'bold';
    timeLabel.style.color = '#38bdf8';
    timeLabel.style.minWidth = '75px';
    timeLabel.style.textAlign = 'right';

    scrubberRow.appendChild(playBtn);
    scrubberRow.appendChild(slider);
    scrubberRow.appendChild(timeLabel);

    // Quick jump buttons
    const quickRow = document.createElement('div');
    quickRow.style.display = 'flex';
    quickRow.style.justifyContent = 'space-between';
    quickRow.style.alignItems = 'center';
    quickRow.style.flexWrap = 'wrap';
    quickRow.style.gap = '6px';
    quickRow.style.fontSize = '0.72rem';

    const jumpGroup = document.createElement('div');
    jumpGroup.style.display = 'flex';
    jumpGroup.style.gap = '6px';

    const btnNow = this.createQuickBtn('Now', 0);
    const btnMaxInv = document.createElement('button');
    btnMaxInv.textContent = 'Max Inversion';
    btnMaxInv.style.padding = '2px 8px';
    btnMaxInv.style.fontSize = '0.72rem';
    btnMaxInv.style.borderRadius = '4px';
    btnMaxInv.style.border = '1px solid rgba(245, 158, 11, 0.4)';
    btnMaxInv.style.background = 'rgba(245, 158, 11, 0.15)';
    btnMaxInv.style.color = '#fbbf24';
    btnMaxInv.style.cursor = 'pointer';
    btnMaxInv.onclick = () => this.jumpToMaxInversion();

    jumpGroup.appendChild(btnNow);
    jumpGroup.appendChild(btnMaxInv);

    const legendGroup = document.createElement('div');
    legendGroup.style.display = 'flex';
    legendGroup.style.gap = '12px';
    legendGroup.style.alignItems = 'center';
    legendGroup.innerHTML = `
      <span style="display: flex; align-items: center; gap: 4px;">
        <span style="display: inline-block; width: 10px; height: 3px; background: #ef4444; border-radius: 1px;"></span>
        <span style="color: #cbd5e1;">Temp T</span>
      </span>
      <span style="display: flex; align-items: center; gap: 4px;">
        <span style="display: inline-block; width: 10px; height: 3px; background: #06b6d4; border-radius: 1px;"></span>
        <span style="color: #cbd5e1;">Dew Point Td</span>
      </span>
      <span style="display: flex; align-items: center; gap: 4px;">
        <span style="display: inline-block; width: 10px; height: 8px; background: rgba(56, 189, 248, 0.35); border-radius: 1px;"></span>
        <span style="color: #cbd5e1;">Cloud/Moist</span>
      </span>
      <span style="display: flex; align-items: center; gap: 4px;">
        <span style="display: inline-block; width: 10px; height: 8px; background: rgba(245, 158, 11, 0.25); border-radius: 1px;"></span>
        <span style="color: #cbd5e1;">Inversion</span>
      </span>
    `;

    quickRow.appendChild(jumpGroup);
    quickRow.appendChild(legendGroup);

    controlsEl.appendChild(scrubberRow);
    controlsEl.appendChild(quickRow);
    this.container.appendChild(controlsEl);

    // Canvas Container
    const canvasContainer = document.createElement('div');
    canvasContainer.id = 'sounding-canvas-container';
    canvasContainer.style.position = 'relative';
    canvasContainer.style.width = '100%';
    canvasContainer.style.height = '340px';
    canvasContainer.style.background = '#0a0f1d';
    canvasContainer.style.borderRadius = '8px';
    canvasContainer.style.border = '1px solid rgba(255, 255, 255, 0.08)';
    canvasContainer.style.overflow = 'hidden';

    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';
    canvasContainer.appendChild(this.canvas);
    this.container.appendChild(canvasContainer);

    this.ctx = this.canvas.getContext('2d');

    // Attach canvas mouse interaction
    this.attachCanvasListeners();

    // Summary Table
    const tableContainer = document.createElement('div');
    tableContainer.id = 'sounding-table-container';
    tableContainer.style.overflowX = 'auto';
    tableContainer.style.background = 'rgba(0, 0, 0, 0.2)';
    tableContainer.style.borderRadius = '6px';
    tableContainer.style.border = '1px solid rgba(255, 255, 255, 0.05)';
    this.container.appendChild(tableContainer);

    // Resize observer to keep canvas sharp
    this.resizeObserver = new ResizeObserver(() => {
      this.drawChart();
    });
    this.resizeObserver.observe(canvasContainer);

    this.updateControls();
    this.drawChart();
    this.updateSummaryTable();
  }

  private createQuickBtn(label: string, hourIdx: number): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.style.padding = '2px 8px';
    btn.style.fontSize = '0.72rem';
    btn.style.borderRadius = '4px';
    btn.style.border = '1px solid rgba(255, 255, 255, 0.15)';
    btn.style.background = 'rgba(255, 255, 255, 0.05)';
    btn.style.color = '#cbd5e1';
    btn.style.cursor = 'pointer';
    btn.onclick = () => this.setHour(hourIdx);
    return btn;
  }

  private togglePlay(): void {
    if (this.isPlaying) {
      if (this.playIntervalId !== null) {
        window.clearInterval(this.playIntervalId);
        this.playIntervalId = null;
      }
      this.isPlaying = false;
      const playBtn = this.container.querySelector('#sounding-play-btn');
      if (playBtn) playBtn.textContent = 'Play';
    } else {
      this.isPlaying = true;
      const playBtn = this.container.querySelector('#sounding-play-btn');
      if (playBtn) playBtn.textContent = 'Pause';

      this.playIntervalId = window.setInterval(() => {
        let next = this.activeHourIdx + 1;
        if (next >= this.soundings.length) next = 0;
        this.setHour(next);
      }, 800);
    }
  }

  private jumpToMaxInversion(): void {
    let maxDiff = -999;
    let bestIdx = 0;
    for (let i = 0; i < this.inversionMetrics.length; i++) {
      if (this.inversionMetrics[i].inversionDiffF > maxDiff) {
        maxDiff = this.inversionMetrics[i].inversionDiffF;
        bestIdx = i;
      }
    }
    this.setHour(bestIdx);
  }

  private updateControls(): void {
    const slider = this.container.querySelector('#sounding-slider') as HTMLInputElement | null;
    if (slider) {
      slider.value = String(this.activeHourIdx);
    }

    const timeDisplay = this.container.querySelector('#sounding-time-display');
    const col = this.soundings[this.activeHourIdx];
    if (timeDisplay && col) {
      timeDisplay.textContent = col.timeLabel || `Hour ${this.activeHourIdx}`;
    }

    const badgeEl = this.container.querySelector('#sounding-inversion-badge');
    const inv = this.inversionMetrics[this.activeHourIdx];
    if (badgeEl && inv) {
      if (inv.active && inv.inversionDiffF > 0.5) {
        badgeEl.innerHTML = `
          <div style="display: flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 6px; background: rgba(245, 158, 11, 0.2); border: 1px solid rgba(245, 158, 11, 0.5); font-size: 0.78rem; font-weight: 600; color: #fbbf24;">
            <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #f59e0b;"></span>
            <span>INVERSION DETECTED: +${inv.inversionDiffF.toFixed(1)}°F aloft (Cap: ${inv.inversionCapFt.toLocaleString()} ft)</span>
          </div>
        `;
      } else {
        badgeEl.innerHTML = `
          <div style="display: flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 6px; background: rgba(59, 130, 246, 0.15); border: 1px solid rgba(59, 130, 246, 0.3); font-size: 0.78rem; color: #93c5fd;">
            <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #3b82f6;"></span>
            <span>Normal Lapse: ${inv.lapseRateFPer1k.toFixed(1)}°F / 1k ft</span>
          </div>
        `;
      }
    }
  }

  private drawChart(): void {
    if (!this.canvas || !this.ctx) return;
    const canvas = this.canvas;
    const ctx = this.ctx;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx.resetTransform();
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;

    ctx.clearRect(0, 0, w, h);

    const padLeft = 85;
    const padRight = 70; // space for wind barbs
    const padTop = 25;
    const padBottom = 35;

    const plotW = w - padLeft - padRight;
    const plotH = h - padTop - padBottom;

    if (plotW <= 0 || plotH <= 0) return;

    const pMin = 300;
    const pMax = 1000;
    const logPMin = Math.log(pMin);
    const logPMax = Math.log(pMax);

    const getYForP = (p: number) => {
      const clampedP = Math.max(pMin, Math.min(pMax, p));
      const frac = (Math.log(clampedP) - logPMin) / (logPMax - logPMin);
      // pMax (1000 hPa) at bottom, pMin (300 hPa) at top
      return padTop + (1.0 - frac) * plotH;
    };

    const col = this.soundings[this.activeHourIdx];
    if (!col) return;

    // Gather points [pressure, tempVal, dewVal, heightFt]
    interface SoundingNode {
      p: number;
      t: number;
      td: number;
      hFt: number;
      rh: number;
      wspd: number;
      wdir: number;
    }

    const nodes: SoundingNode[] = [];
    const sfcP = 1013.25 * Math.pow(1 - (col.surface.elevationM / 44330.0), 5.255);
    const sfcT = this.isImperial ? col.surface.tempF : col.surface.tempC;
    const sfcTd = this.isImperial ? col.surface.dewPointF : col.surface.dewPointC;

    nodes.push({
      p: sfcP,
      t: sfcT,
      td: sfcTd,
      hFt: col.surface.elevationFt,
      rh: col.surface.relativeHumidity,
      wspd: col.surface.windSpeedMph,
      wdir: col.surface.windDirDeg
    });

    for (const lev of col.levels) {
      if (lev.levelHpa <= sfcP) {
        nodes.push({
          p: lev.levelHpa,
          t: this.isImperial ? lev.tempF : lev.tempC,
          td: this.isImperial ? lev.dewPointF : lev.dewPointC,
          hFt: lev.heightFt,
          rh: lev.relativeHumidity,
          wspd: lev.windSpeedMph,
          wdir: lev.windDirDeg
        });
      }
    }

    // Determine temperature domain (minT, maxT)
    let minT = 0;
    let maxT = 100;
    if (!this.isImperial) {
      minT = -30;
      maxT = 40;
    }

    for (const n of nodes) {
      minT = Math.min(minT, n.td - 5, n.t - 5);
      maxT = Math.max(maxT, n.t + 10);
    }
    // Round out to nice increments of 10
    minT = Math.floor(minT / 10) * 10;
    maxT = Math.ceil(maxT / 10) * 10;

    const getXForT = (t: number) => {
      const frac = (t - minT) / (maxT - minT);
      return padLeft + frac * plotW;
    };

    // Draw background grid lines for pressure levels
    const pressureTicks = [1000, 925, 850, 700, 500, 300];
    const stdHeightsFt: Record<number, number> = {
      1000: 360,
      925: 2500,
      850: 5000,
      700: 10000,
      500: 18000,
      300: 30000
    };

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    ctx.fillStyle = '#64748b';
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    for (const p of pressureTicks) {
      const y = getYForP(p);
      ctx.beginPath();
      ctx.moveTo(padLeft, y);
      ctx.lineTo(padLeft + plotW, y);
      ctx.stroke();

      const altStr = `${stdHeightsFt[p].toLocaleString()}ft`;
      ctx.fillText(`${p}mb (${altStr})`, padLeft - 6, y);
    }

    // Draw temperature grid lines
    const tStep = this.isImperial ? 20 : 10;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    for (let t = minT; t <= maxT; t += tStep) {
      const x = getXForT(t);
      if (x < padLeft || x > padLeft + plotW) continue;

      ctx.beginPath();
      ctx.moveTo(x, padTop);
      ctx.lineTo(x, padTop + plotH);
      ctx.strokeStyle = t === (this.isImperial ? 32 : 0) ? 'rgba(56, 189, 248, 0.35)' : 'rgba(255, 255, 255, 0.05)';
      ctx.lineWidth = t === (this.isImperial ? 32 : 0) ? 1.5 : 1;
      ctx.stroke();

      ctx.fillStyle = t === (this.isImperial ? 32 : 0) ? '#38bdf8' : '#64748b';
      ctx.fillText(`${t}°`, x, padTop + plotH + 6);
    }

    // Highlight Freezing Level Line (32 F / 0 C)
    const freezeT = this.isImperial ? 32 : 0;
    const freezeX = getXForT(freezeT);
    if (freezeX >= padLeft && freezeX <= padLeft + plotW) {
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.moveTo(freezeX, padTop);
      ctx.lineTo(freezeX, padTop + plotH);
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.5)';
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Inversion Layer Highlight
    for (let i = 0; i < nodes.length - 1; i++) {
      const n1 = nodes[i];
      const n2 = nodes[i + 1];
      if (n2.t > n1.t && n2.hFt > n1.hFt) {
        const y1 = getYForP(n1.p);
        const y2 = getYForP(n2.p);
        ctx.fillStyle = 'rgba(245, 158, 11, 0.12)';
        ctx.fillRect(padLeft, y2, plotW, y1 - y2);

        ctx.strokeStyle = 'rgba(245, 158, 11, 0.35)';
        ctx.setLineDash([3, 3]);
        ctx.strokeRect(padLeft, y2, plotW, y1 - y2);
        ctx.setLineDash([]);

        ctx.fillStyle = '#f59e0b';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('INVERSION LAYER', padLeft + 8, (y1 + y2) / 2);
      }
    }

    // Moisture / Cloud Layer Shading (T - Td <= 2.5 C or 4.5 F)
    const depressionThresh = this.isImperial ? 4.5 : 2.5;
    for (let i = 0; i < nodes.length - 1; i++) {
      const n1 = nodes[i];
      const n2 = nodes[i + 1];
      const dep1 = n1.t - n1.td;
      const dep2 = n2.t - n2.td;
      if (dep1 <= depressionThresh || dep2 <= depressionThresh) {
        const x1Td = getXForT(n1.td);
        const x1T = getXForT(n1.t);
        const y1 = getYForP(n1.p);
        const x2Td = getXForT(n2.td);
        const x2T = getXForT(n2.t);
        const y2 = getYForP(n2.p);

        ctx.beginPath();
        ctx.moveTo(x1Td, y1);
        ctx.lineTo(x2Td, y2);
        ctx.lineTo(x2T, y2);
        ctx.lineTo(x1T, y1);
        ctx.closePath();
        ctx.fillStyle = 'rgba(56, 189, 248, 0.22)';
        ctx.fill();
      }
    }

    // Terrain Elevation Lines
    const drawElevLine = (elevFt: number, label: string, color: string) => {
      // Find approximate pressure for this elevation
      const pApprox = 1013.25 * Math.pow(1 - ((elevFt / 3.28084) / 44330.0), 5.255);
      if (pApprox >= pMin && pApprox <= pMax) {
        const y = getYForP(pApprox);
        ctx.beginPath();
        ctx.setLineDash([2, 4]);
        ctx.moveTo(padLeft, y);
        ctx.lineTo(padLeft + plotW, y);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = color;
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(label, padLeft + plotW - 4, y - 3);
      }
    };

    drawElevLine(this.minElevFt, `Valley Floor (${this.minElevFt.toLocaleString()}ft)`, 'rgba(148, 163, 184, 0.5)');
    drawElevLine(this.poiElevFt, `POI Ground (${this.poiElevFt.toLocaleString()}ft)`, 'rgba(52, 211, 153, 0.6)');
    if (this.maxElevFt > this.poiElevFt + 300) {
      drawElevLine(this.maxElevFt, `Peak / Ridge (${this.maxElevFt.toLocaleString()}ft)`, 'rgba(248, 113, 113, 0.6)');
    }

    // Freezing Level Line
    if (col.freezingLevelFt > 0) {
      const pFreeze = 1013.25 * Math.pow(1 - ((col.freezingLevelFt / 3.28084) / 44330.0), 5.255);
      if (pFreeze >= pMin && pFreeze <= pMax) {
        const y = getYForP(pFreeze);
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.moveTo(padLeft, y);
        ctx.lineTo(padLeft + plotW, y);
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 1.2;
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#38bdf8';
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(`Freezing Level (~${col.freezingLevelFt.toLocaleString()}ft)`, padLeft + 6, y - 3);
      }
    }

    // Draw Dew Point Profile (Cyan)
    ctx.beginPath();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#06b6d4';
    nodes.forEach((n, idx) => {
      const x = getXForT(n.td);
      const y = getYForP(n.p);
      if (idx === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Draw Temperature Profile (Red)
    ctx.beginPath();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#ef4444';
    nodes.forEach((n, idx) => {
      const x = getXForT(n.t);
      const y = getYForP(n.p);
      if (idx === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Draw Node Dots
    for (const n of nodes) {
      const y = getYForP(n.p);
      const xT = getXForT(n.t);
      const xTd = getXForT(n.td);

      // Dew Point Dot
      ctx.beginPath();
      ctx.arc(xTd, y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = '#06b6d4';
      ctx.fill();
      ctx.strokeStyle = '#083344';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Temp Dot
      ctx.beginPath();
      ctx.arc(xT, y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      ctx.strokeStyle = '#450a0a';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Draw Wind Barbs on Right Spine
    const barbX = padLeft + plotW + 35;
    for (const n of nodes) {
      const y = getYForP(n.p);
      this.drawWindBarb(ctx, barbX, y, n.wspd, n.wdir);
    }

    // Right Spine Header
    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('WINDS', barbX, padTop - 12);

    // Plot Border
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1;
    ctx.strokeRect(padLeft, padTop, plotW, plotH);

    // Draw Active Hover Tooltip if present
    if (this.hoverNode) {
      const hNode = this.hoverNode;
      const boxW = 160;
      const boxH = hNode.text.length * 14 + 14;
      let bx = hNode.x + 12;
      let by = hNode.y - boxH / 2;

      if (bx + boxW > w - 10) bx = hNode.x - boxW - 12;
      if (by < 10) by = 10;
      if (by + boxH > h - 10) by = h - boxH - 10;

      ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(bx, by, boxW, boxH, 6);
      ctx.fill();
      ctx.stroke();

      ctx.textAlign = 'left';
      ctx.font = '10px monospace';
      hNode.text.forEach((txt, idx) => {
        ctx.fillStyle = idx === 0 ? '#38bdf8' : (idx === 1 ? '#f87171' : (idx === 2 ? '#22d3ee' : '#cbd5e1'));
        ctx.fillText(txt, bx + 8, by + 16 + idx * 14);
      });
    }
  }

  private drawWindBarb(ctx: CanvasRenderingContext2D, cx: number, cy: number, speedMph: number, dirDeg: number): void {
    const kts = speedMph / 1.15078;
    const len = 22;
    // Standard meteorological convention: staff points into the direction wind is blowing FROM
    const rad = ((dirDeg + 180) % 360) * Math.PI / 180;

    const xEnd = cx + Math.sin(rad) * len;
    const yEnd = cy - Math.cos(rad) * len;

    ctx.save();
    ctx.strokeStyle = '#94a3b8';
    ctx.fillStyle = '#94a3b8';
    ctx.lineWidth = 1.2;

    // Small center circle
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.stroke();

    if (kts < 3) {
      // Calm: circle only
      ctx.restore();
      return;
    }

    // Staff line
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(xEnd, yEnd);
    ctx.stroke();

    // Angle perpendicular to staff for flags and ticks
    const pAngle = rad + Math.PI / 2;
    const tickLen = 7;
    let remKts = Math.round(kts);
    let curFrac = 1.0;

    // 50-kt pennants
    while (remKts >= 48) {
      const px = cx + Math.sin(rad) * (len * curFrac);
      const py = cy - Math.cos(rad) * (len * curFrac);
      curFrac -= 0.18;
      const pxBase = cx + Math.sin(rad) * (len * curFrac);
      const pyBase = cy - Math.cos(rad) * (len * curFrac);

      const tipX = px + Math.sin(pAngle) * tickLen * 1.3;
      const tipY = py - Math.cos(pAngle) * tickLen * 1.3;

      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(tipX, tipY);
      ctx.lineTo(pxBase, pyBase);
      ctx.closePath();
      ctx.fill();

      remKts -= 50;
      curFrac -= 0.05;
    }

    // 10-kt full ticks
    while (remKts >= 8) {
      const px = cx + Math.sin(rad) * (len * curFrac);
      const py = cy - Math.cos(rad) * (len * curFrac);
      const tx = px + Math.sin(pAngle) * tickLen;
      const ty = py - Math.cos(pAngle) * tickLen;

      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(tx, ty);
      ctx.stroke();

      remKts -= 10;
      curFrac -= 0.15;
    }

    // 5-kt half tick
    if (remKts >= 3) {
      const px = cx + Math.sin(rad) * (len * curFrac);
      const py = cy - Math.cos(rad) * (len * curFrac);
      const tx = px + Math.sin(pAngle) * (tickLen * 0.55);
      const ty = py - Math.cos(pAngle) * (tickLen * 0.55);

      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(tx, ty);
      ctx.stroke();
    }

    ctx.restore();

    // Speed readout label next to barb
    ctx.fillStyle = '#cbd5e1';
    ctx.font = '8px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${Math.round(speedMph)}mph`, cx + 16, cy);
  }

  private attachCanvasListeners(): void {
    if (!this.canvas) return;

    this.canvas.onmousemove = (e) => {
      const rect = this.canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      const col = this.soundings[this.activeHourIdx];
      if (!col) return;

      const padLeft = 85;
      const plotW = rect.width - 85 - 70;
      const plotH = rect.height - 25 - 35;
      const padTop = 25;

      const logPMin = Math.log(300);
      const logPMax = Math.log(1000);

      // Check proximity to any node
      let found: { x: number; y: number; text: string[] } | null = null;
      let bestDist = 18;

      const allLevels: Array<{ name: string; p: number; hFt: number; t: number; td: number; rh: number; wspd: number; wdir: number }> = [
        {
          name: 'Surface',
          p: Math.round(1013.25 * Math.pow(1 - (col.surface.elevationM / 44330.0), 5.255)),
          hFt: col.surface.elevationFt,
          t: this.isImperial ? col.surface.tempF : col.surface.tempC,
          td: this.isImperial ? col.surface.dewPointF : col.surface.dewPointC,
          rh: col.surface.relativeHumidity,
          wspd: col.surface.windSpeedMph,
          wdir: col.surface.windDirDeg
        },
        ...col.levels.map(l => ({
          name: `${l.levelHpa} hPa`,
          p: l.levelHpa,
          hFt: l.heightFt,
          t: this.isImperial ? l.tempF : l.tempC,
          td: this.isImperial ? l.dewPointF : l.dewPointC,
          rh: l.relativeHumidity,
          wspd: l.windSpeedMph,
          wdir: l.windDirDeg
        }))
      ];

      for (const lev of allLevels) {
        const frac = (Math.log(Math.max(300, Math.min(1000, lev.p))) - logPMin) / (logPMax - logPMin);
        const y = padTop + (1.0 - frac) * plotH;

        const distY = Math.abs(my - y);
        if (distY < bestDist && mx >= padLeft && mx <= padLeft + plotW + 40) {
          bestDist = distY;
          const unit = this.isImperial ? '°F' : '°C';
          found = {
            x: mx,
            y: y,
            text: [
              `${lev.name} (${lev.hFt.toLocaleString()} ft)`,
              `Temp: ${lev.t.toFixed(1)}${unit}`,
              `Dew Point: ${lev.td.toFixed(1)}${unit} (RH ${lev.rh}%)`,
              `Wind: ${Math.round(lev.wspd)} mph @ ${Math.round(lev.wdir)}°`
            ]
          };
        }
      }

      this.hoverNode = found;
      this.drawChart();
    };

    this.canvas.onmouseleave = () => {
      this.hoverNode = null;
      this.drawChart();
    };
  }

  private updateSummaryTable(): void {
    const tableContainer = this.container.querySelector('#sounding-table-container');
    if (!tableContainer) return;

    const col = this.soundings[this.activeHourIdx];
    if (!col) return;

    const unit = this.isImperial ? '°F' : '°C';

    const rows: Array<{
      level: string;
      elev: string;
      temp: string;
      dew: string;
      rh: string;
      wind: string;
      inversion: string;
    }> = [];

    // Surface row
    const sfcT = this.isImperial ? col.surface.tempF : col.surface.tempC;
    const sfcTd = this.isImperial ? col.surface.dewPointF : col.surface.dewPointC;
    rows.push({
      level: 'Surface Ground',
      elev: `${col.surface.elevationFt.toLocaleString()} ft (${col.surface.elevationM} m)`,
      temp: `${sfcT.toFixed(1)} ${unit}`,
      dew: `${sfcTd.toFixed(1)} ${unit}`,
      rh: `${col.surface.relativeHumidity}%`,
      wind: `${Math.round(col.surface.windSpeedMph)} mph @ ${Math.round(col.surface.windDirDeg)}°`,
      inversion: 'Surface Base'
    });

    // Isobaric rows
    for (const lev of col.levels) {
      const t = this.isImperial ? lev.tempF : lev.tempC;
      const td = this.isImperial ? lev.dewPointF : lev.dewPointC;
      const isInv = t > sfcT;
      const diffF = (t - sfcT) * (this.isImperial ? 1 : 1.8);
      const invStr = isInv
        ? `<span style="color: #fbbf24; font-weight: bold;">+${diffF.toFixed(1)}°F Aloft</span>`
        : `<span style="color: #94a3b8;">${diffF.toFixed(1)}°F</span>`;

      rows.push({
        level: `${lev.levelHpa} hPa`,
        elev: `${lev.heightFt.toLocaleString()} ft (${lev.heightM} m)`,
        temp: `${t.toFixed(1)} ${unit}`,
        dew: `${td.toFixed(1)} ${unit}`,
        rh: `${lev.relativeHumidity}%`,
        wind: `${Math.round(lev.windSpeedMph)} mph @ ${Math.round(lev.windDirDeg)}°`,
        inversion: invStr
      });
    }

    tableContainer.innerHTML = `
      <table style="width: 100%; border-collapse: collapse; font-size: 0.73rem; text-align: left; font-family: monospace;">
        <thead>
          <tr style="border-bottom: 1px solid rgba(255, 255, 255, 0.1); color: #94a3b8;">
            <th style="padding: 6px 8px;">Level</th>
            <th style="padding: 6px 8px;">Altitude (MSL)</th>
            <th style="padding: 6px 8px;">Temp</th>
            <th style="padding: 6px 8px;">Dew Pt</th>
            <th style="padding: 6px 8px;">RH</th>
            <th style="padding: 6px 8px;">Wind Aloft</th>
            <th style="padding: 6px 8px;">Inversion Delta</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(r => `
            <tr style="border-bottom: 1px solid rgba(255, 255, 255, 0.04);">
              <td style="padding: 5px 8px; font-weight: bold; color: #f8fafc;">${r.level}</td>
              <td style="padding: 5px 8px; color: #cbd5e1;">${r.elev}</td>
              <td style="padding: 5px 8px; color: #f87171; font-weight: 600;">${r.temp}</td>
              <td style="padding: 5px 8px; color: #22d3ee;">${r.dew}</td>
              <td style="padding: 5px 8px; color: #cbd5e1;">${r.rh}</td>
              <td style="padding: 5px 8px; color: #93c5fd;">${r.wind}</td>
              <td style="padding: 5px 8px;">${r.inversion}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  }
}
